import {
	API_FETCH_ABORT_SYMBOL,
	API_FORBIDDEN_EXCEPTION_ERROR,
	API_GLOBAL_ERROR,
	API_LOCAL_ERROR,
} from './symbols';
import { describeApiFailure } from './apiWrapperError.utils';
import type { SessionEndReason } from '@dealstack/shared/lib/common/session/sessionContract';
import type { ErrorCode } from '@dealstack/shared/lib/common/errors';

export type ICommonApi<Result> = {
	response?: Result;
	/**
	 * If request is successfull then returns response, else returns undefiend
	 */
	concludeWithPromise: () => Promise<Result | undefined>;
	onSuccess: (callback: ICallbacks<Result>['onSuccess']) => ICommonApi<Result>;
	onLocalError: (callback: ICallbacks<Result>['onLocalError']) => ICommonApi<Result>;
	onGlobalError: (callback: ICallbacks<Result>['onGlobalError']) => ICommonApi<Result>;
	onAnyError: (callback: ICallbacks<Result>['onAnyError']) => ICommonApi<Result>;
	onUnsafeFinish: (callback: ICallbacks<Result>['onUnsafeFinish']) => ICommonApi<Result>;
	onComponentUnmountedAndRequestFinished: (
		callback: ICallbacks<Result>['onComponentUnmountedAndRequestFinished']
	) => ICommonApi<Result>;
	onFinish: (callback: ICallbacks<Result>['onFinish']) => ICommonApi<Result>;
	onPromiseCancel: (callback: ICallbacks<Result>['onPromiseCancel']) => ICommonApi<Result>;
};

export interface ServiceRequestOptions {
	abortController?: AbortController | null;
	abortSignal?: AbortSignal;
	skipErrorHandler?: boolean;
	/**
	 * Suppress the default 401 → login-page redirect. Required on public
	 * (unauthenticated) pages — e.g. token-based public signing — where a 401
	 * is an expected outcome (invalid/expired/mismatched token) and the visitor
	 * has no account to log in to.
	 */
	skipLoginRedirect?: boolean;
}

export type ServiceRequest<ArgumentType, ReturnType> = (
	args: ArgumentType,
	options?: ServiceRequestOptions
) => Promise<ReturnType>;

export type MakeRequestOptions =
	| undefined
	| {
			disableDefaultLocalErrorHandler?: boolean;
	  };

export type WrapRequestOptions<Result> =
	| undefined
	| {
			onSuccessDefault?: NonNullable<ICallbacks<Result>['onSuccess']>;
			onLocalErrorDefault?: NonNullable<ICallbacks<Result>['onLocalError']>;
			onAnyErrorDefault?: NonNullable<ICallbacks<Result>['onAnyError']>;
			onGlobalErrorDefault?: NonNullable<ICallbacks<Result>['onGlobalError']>;
	  };

/**
 * Extends `Error` so that a failed request reaches Sentry as an exception with a
 * name, a message and a stack. As a plain object it was captured as "Object
 * captured as exception with keys: error, symbol", which degraded the issue title
 * to the minified name of whichever function called `captureException`.
 *
 * `message` is an accessor because `createErrorInstance` populates `status` and
 * `error` after construction; deriving it on read keeps it from going stale.
 * It is diagnostic, not user-facing — use `getUserFacingErrorMessage` for that.
 */
export class ApiWrapperError<T = string> extends Error {
	constructor(
		symbol:
			| typeof API_FETCH_ABORT_SYMBOL
			| typeof API_GLOBAL_ERROR
			| typeof API_LOCAL_ERROR
			| typeof API_FORBIDDEN_EXCEPTION_ERROR,
		error?: T
	) {
		super();
		// defineProperty, not assignment: `useDefineForClassFields` would make an
		// assigned `name` an enumerable own property, and it would then show up in
		// every `{...err}` and `JSON.stringify(err)` of a caught failure.
		Object.defineProperty(this, 'name', {
			value: 'ApiWrapperError',
			enumerable: false,
			writable: true,
			configurable: true,
		});
		this.symbol = symbol;
		this.error = error;
	}

	/** `#private`, so it stays out of serialized payloads. */
	#explicitMessage: string | undefined;

	override get message(): string {
		return this.#explicitMessage ?? describeApiFailure(this);
	}

	override set message(value: string) {
		this.#explicitMessage = value;
	}

	symbol:
		| typeof API_FETCH_ABORT_SYMBOL
		| typeof API_GLOBAL_ERROR
		| typeof API_LOCAL_ERROR
		| typeof API_FORBIDDEN_EXCEPTION_ERROR
		| undefined;

	error: T | undefined;

	/**
	 * Which call failed, with id-shaped path segments collapsed. Every ApiWrapperError
	 * is constructed at one line inside an async catch block, so its stack is the same
	 * two frames for every endpoint in the app — this is what lets Sentry tell them
	 * apart. Absent when the failure was built without the request params.
	 */
	request?: { method: string; endpoint: string };

	/** Set when `reportTransportError` already sent this failure to Sentry itself. */
	alreadyReported?: boolean;

	/**
	 * HTTP status of the underlying Response, when the failure came from one.
	 * Lets callers distinguish a definitive rejection (e.g. 401 wrong-scope
	 * token) from a transient/network error, rather than treating every failure
	 * the same.
	 */
	status?: number;

	/**
	 * Milliseconds the client should wait before retrying, parsed from the
	 * `Retry-After` header of a 429 response. Undefined when not rate-limited or
	 * when the header is absent/non-numeric.
	 */
	retryAfterMs?: number;

	/**
	 * Why the server says the session ended, from the 401 that ended it. Lets a caller
	 * that suppresses the automatic redirect still explain the right thing, rather than
	 * guessing a reason the server already told us.
	 */
	sessionEndReason?: SessionEndReason;
}

type StandardBackendError = ApiWrapperError<ErrorCode> & { error: ErrorCode };

/**
 * Narrows a thrown value to a backend `BusinessException` payload (`{ error: { code, message } }`).
 * Lives beside `ApiWrapperError` rather than in `apiLayer` so pure helpers can narrow an error
 * without importing the request layer.
 */
export const isStandardBackendError = (error: unknown): error is StandardBackendError => {
	if (typeof error !== 'object') {
		return false;
	}
	if (!(error as Record<string, unknown>)?.symbol) {
		return false;
	}
	if (!(error as Record<string, Record<string, unknown>>)?.error?.code) {
		return false;
	}
	if (!(error as Record<string, Record<string, unknown>>)?.error?.message) {
		return false;
	}
	return true;
};

export interface ICallbacks<Result> {
	onSuccess?: (result: Result) => unknown;
	onLocalError?: (error: unknown) => unknown;
	onGlobalError?: (error: unknown) => unknown;
	onAnyError?: (error: unknown) => unknown;
	onComponentUnmountedAndRequestFinished?: (result: unknown) => unknown;
	onUnsafeFinish?: () => unknown;
	onFinish?: () => unknown;
	onPromiseCancel?: (error: ApiWrapperError) => unknown;
}

export type ErrorHanlder = (error: unknown) => unknown;

export type ApiWrapperState = {
	commonLocalErrorHandler?: ErrorHanlder;
	commonGlobalErrorHandler?: ErrorHanlder;
};

export interface ApiWrapperSetupOptions {
	commonLocalErrorHandler?: ErrorHanlder;
	commonGlobalErrorHandler?: ErrorHanlder;
}

export interface IComponentStatus {
	alive: boolean;
}

export interface MakeRequest<ArgumentType = void, ResultType = unknown> {
	isLoading: boolean;
	isError: boolean;
	fetch: (args: ArgumentType) => ICommonApi<ResultType>;
	response: ResultType;
	abortController: AbortController;
	maybeAbort: () => void;
	resetInstance: () => void;
}
